import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { OverviewTab } from '../components/workspace/OverviewTab';
import { PlanTab } from '../components/workspace/PlanTab';
import { ProgressTab } from '../components/workspace/ProgressTab';
import { QualityTab } from '../components/workspace/QualityTab';
import { FilesTab } from '../components/workspace/FilesTab';
import { NotesTab } from '../components/workspace/NotesTab';
import { MoneyTab } from '../components/workspace/MoneyTab';
import { ShareLinkButton } from '../components/workspace/ShareLinkButton';
import { useMe, useProjectDashboard, useUpdateProjectStatus } from '../hooks/useOps';
import { formatDate, humanizeStatus, statusColor } from '../lib/format';

const TABS = ['overview', 'plan', 'progress', 'quality', 'files', 'notes', 'money'] as const;
type WorkspaceTab = (typeof TABS)[number];

/** Mirror of the backend PROJECT_TRANSITIONS table — keep in sync. */
const PROJECT_TRANSITIONS: Record<string, string[]> = {
  draft: ['active', 'cancelled'],
  active: ['delivered', 'cancelled'],
  delivered: ['closed', 'active'],
  closed: [],
  cancelled: [],
};

/**
 * The one project workspace (projects v2, W5). Every project action lives here
 * under one of six tabs; the old Hub/Setup/TrackDetail pages redirect in.
 */
export function ProjectWorkspacePage() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const activeTab: WorkspaceTab = TABS.includes(tab as WorkspaceTab)
    ? (tab as WorkspaceTab)
    : 'overview';

  const { data, isLoading, error } = useProjectDashboard(id);
  const { data: me } = useMe();
  // Money is cash visibility — the tab simply doesn't exist without it.
  const canSeeMoney = Boolean(me?.capability.canSeeCash);
  const updateStatus = useUpdateProjectStatus();
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [confirmStatus, setConfirmStatus] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data || !id) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {(error as Error)?.message ?? 'Failed to load project'}
      </Alert>
    );
  }

  const { project } = data;
  const nextStatuses = PROJECT_TRANSITIONS[project.status] ?? [];

  const applyStatus = async (status: string) => {
    setConfirmStatus(null);
    try {
      await updateStatus.mutateAsync({ id, status });
    } catch (e) {
      setStatusError((e as Error).message);
    }
  };

  return (
    <Box>
      <PageHeader
        title={`${project.code} — ${project.name}`}
        subtitle={
          project.actualEndDate
            ? `Delivered ${formatDate(project.actualEndDate)}`
            : undefined
        }
        action={
          <Stack direction="row" gap={1} alignItems="center">
            <Chip
              label={humanizeStatus(project.status)}
              color={statusColor(project.status)}
              variant="outlined"
              deleteIcon={<ArrowDropDownIcon />}
              onDelete={nextStatuses.length ? (e) => setStatusAnchor(e.currentTarget.parentElement) : undefined}
              onClick={nextStatuses.length ? (e) => setStatusAnchor(e.currentTarget) : undefined}
            />
            <Menu
              anchorEl={statusAnchor}
              open={Boolean(statusAnchor)}
              onClose={() => setStatusAnchor(null)}
            >
              {nextStatuses.map((s) => (
                <MenuItem
                  key={s}
                  onClick={() => {
                    setStatusAnchor(null);
                    setConfirmStatus(s);
                  }}
                >
                  Mark {humanizeStatus(s)}
                </MenuItem>
              ))}
            </Menu>
            <ShareLinkButton projectId={id} />
            <Button variant="outlined" onClick={() => navigate(`/projects/${id}/report`)}>
              Report
            </Button>
            <Button variant="outlined" onClick={() => navigate('/projects')}>
              ← Projects
            </Button>
          </Stack>
        }
      />

      {statusError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatusError(null)}>
          {statusError}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_e, v: WorkspaceTab) =>
          navigate(v === 'overview' ? `/projects/${id}` : `/projects/${id}/${v}`)
        }
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Plan" value="plan" />
        <Tab label="Progress" value="progress" />
        <Tab label="Quality" value="quality" />
        <Tab label="Files" value="files" />
        <Tab label="Notes" value="notes" />
        {canSeeMoney && <Tab label="Money" value="money" />}
      </Tabs>

      {activeTab === 'overview' && <OverviewTab projectId={id} />}
      {activeTab === 'plan' && <PlanTab projectId={id} />}
      {activeTab === 'progress' && <ProgressTab projectId={id} />}
      {activeTab === 'quality' && <QualityTab projectId={id} />}
      {activeTab === 'files' && <FilesTab projectId={id} />}
      {activeTab === 'notes' && <NotesTab projectId={id} />}
      {activeTab === 'money' && canSeeMoney && <MoneyTab projectId={id} />}

      {/* Status transition confirm — moving a project state is not an everyday click. */}
      <Dialog open={Boolean(confirmStatus)} onClose={() => setConfirmStatus(null)}>
        <DialogTitle>Mark project {humanizeStatus(confirmStatus ?? '')}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {project.code} — {project.name} moves from{' '}
            <strong>{humanizeStatus(project.status)}</strong> to{' '}
            <strong>{humanizeStatus(confirmStatus ?? '')}</strong>.
            {confirmStatus === 'delivered' && ' The delivery date is recorded today.'}
            {(confirmStatus === 'closed' || confirmStatus === 'cancelled') &&
              ' This is a terminal state.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStatus(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => confirmStatus && applyStatus(confirmStatus)}
            disabled={updateStatus.isPending}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
